import { optionalPositiveInteger, optionalTrimmedString } from '../../../lib/routeSearch';
import {
  defaultDemandPartnersFilter,
  demandPartnerPageSizes,
  DemandPartnerSortDirection,
  DemandPartnerStatus,
  isDemandPartnerSortProperty,
  type DemandPartnersFilter,
  type DemandPartnerSortProperty,
} from './model';

export interface DemandPartnersRouteSearch {
  search?: string;
  statuses?: string;
  page?: number;
  size?: number;
  sortProperty?: DemandPartnerSortProperty | null;
  sortDirection?: DemandPartnerSortDirection;
}

export function demandPartnersRouteSearchSchema(
  search: Record<string, unknown>,
): DemandPartnersRouteSearch {
  const searchTerm = optionalTrimmedString(search['search']);
  const statuses = optionalTrimmedString(search['statuses']);
  const page = optionalPositiveInteger(search['page']);
  const parsedSize = optionalPositiveInteger(search['size']);
  const size =
    parsedSize &&
    demandPartnerPageSizes.includes(parsedSize as (typeof demandPartnerPageSizes)[number])
      ? parsedSize
      : undefined;
  const rawSortProperty = optionalTrimmedString(search['sortProperty']);
  const sortProperty =
    rawSortProperty && isDemandPartnerSortProperty(rawSortProperty) ? rawSortProperty : undefined;
  const sortDirection = Object.values(DemandPartnerSortDirection).includes(
    search['sortDirection'] as DemandPartnerSortDirection,
  )
    ? (search['sortDirection'] as DemandPartnerSortDirection)
    : undefined;

  return {
    ...(searchTerm ? { search: searchTerm } : {}),
    ...(statuses ? { statuses } : {}),
    ...(page ? { page } : {}),
    ...(size ? { size } : {}),
    ...(sortProperty ? { sortProperty } : {}),
    ...(search['sortProperty'] === null ? { sortProperty: null } : {}),
    ...(sortDirection ? { sortDirection } : {}),
  };
}

export function parseDemandPartnersRouteSearch(
  search: DemandPartnersRouteSearch,
): DemandPartnersFilter {
  return {
    searchTerm: search.search ?? '',
    statuses: parseStatuses(search.statuses) ?? defaultDemandPartnersFilter.statuses,
    page: search.page ? search.page - 1 : 0,
    size: search.size ?? defaultDemandPartnersFilter.size,
    sortProperty:
      search.sortProperty === null
        ? null
        : (search.sortProperty ?? defaultDemandPartnersFilter.sortProperty),
    sortDirection: search.sortDirection ?? defaultDemandPartnersFilter.sortDirection,
  };
}

export function serializeDemandPartnersRouteSearch(
  filter: DemandPartnersFilter,
): DemandPartnersRouteSearch {
  return {
    ...(filter.searchTerm.trim() ? { search: filter.searchTerm.trim() } : {}),
    ...(!sameStatuses(filter.statuses, defaultDemandPartnersFilter.statuses)
      ? { statuses: filter.statuses.join(',') }
      : {}),
    ...(filter.page ? { page: filter.page + 1 } : {}),
    ...(filter.size !== defaultDemandPartnersFilter.size ? { size: filter.size } : {}),
    ...(filter.sortProperty !== defaultDemandPartnersFilter.sortProperty
      ? { sortProperty: filter.sortProperty }
      : {}),
    ...(filter.sortDirection !== defaultDemandPartnersFilter.sortDirection
      ? { sortDirection: filter.sortDirection }
      : {}),
  };
}

const demandPartnerStatuses: ReadonlySet<string> = new Set(Object.values(DemandPartnerStatus));

function parseStatuses(value?: string) {
  return value
    ?.split(',')
    .filter((status): status is DemandPartnerStatus => demandPartnerStatuses.has(status));
}

function sameStatuses(left: DemandPartnerStatus[], right: DemandPartnerStatus[]) {
  const rightStatuses = new Set(right);
  return left.length === right.length && left.every((status) => rightStatuses.has(status));
}
